import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useEditor, EditorContent, Extension } from '@tiptap/react';
import { DOMParser } from '@tiptap/pm/model';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Typography from '@tiptap/extension-typography';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import { Markdown } from 'tiptap-markdown';
import { Cloud, Loader, GripVertical, History } from 'lucide-react';
import Suggestion from '@tiptap/suggestion';
import { suggestion } from './suggestion';
import EditorToolbar from './EditorToolbar';
import VersionHistoryPanel from './VersionHistoryPanel';
import versionHistoryService from '../../../services/versionHistoryService';
import vaultService from '../../../services/vaultService';
import { ParagraphActionsContext } from './ParagraphNodeView';
import ParagraphWithControls from './ParagraphExtension';
import MediaCardExtension from './MediaCardExtension';
import { getDragState, clearDragState } from '../dragState';

const SmartEditor = forwardRef(({
    initialContent,
    onSave,
    onOpenVault,
    onAddVaultArtifact,
    episodeId,
    onSelectionChange,
    onAddToMariko,
    // New props for vault drag-and-drop
    droppedArtifacts = [],
    onArtifactDrop,
    onRemoveArtifact,
    onClearArtifacts
}, ref) => {
    const [savingStatus, setSavingStatus] = useState('saved'); // 'saved', 'saving', 'unsaved'
    const [, setSelectionUpdate] = useState(0); // Force re-render on selection update
    const [selectedText, setSelectedText] = useState('');
    const [selectionCoords, setSelectionCoords] = useState(null);
    const [isDraggingToVault, setIsDraggingToVault] = useState(false);
    const [isDraggingFromVault, setIsDraggingFromVault] = useState(false);
    const [isVersionHistoryOpen, setIsVersionHistoryOpen] = useState(false);
    const [marikoContext, setMarikoContext] = useState(null); // Context for AI assistant
    const editorInstanceRef = useRef(null);
    const saveTimeoutRef = useRef(null);
    const lastSavedContentRef = useRef(null);
    const isSavingRef = useRef(false);
    const versionSaveTimeoutRef = useRef(null);
    const editorContainerRef = useRef(null);

    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: {
                    levels: [1, 2, 3],
                },
                // Exclude default paragraph since we're using our custom one
                paragraph: false,
            }),
            // Custom paragraph with Notion-style hover controls
            ParagraphWithControls,
            // Media card extension for images, videos, audio, PDFs
            MediaCardExtension,
            Placeholder.configure({
                placeholder: 'Type \'/\' for commands...',
            }),
            TaskList,
            TaskItem.configure({
                nested: true,
            }),
            Typography,
            TextStyle,
            Color,
            Highlight.configure({
                multicolor: true,
            }),
            Markdown.configure({
                transformPastedText: true,
                transformCopiedText: true,
            }), // Enable Markdown support with copy/paste transformation
            // Register Slash Commands
            Extension.create({
                name: 'slash-command',
                addOptions() {
                    return {
                        suggestion: {
                            char: '/',
                            command: ({ editor, range, props }) => {
                                props.command({ editor, range });
                            },
                        },
                    };
                },
                addProseMirrorPlugins() {
                    return [
                        Suggestion({
                            editor: this.editor,
                            ...this.options.suggestion,
                        }),
                    ];
                },
            }).configure({
                suggestion,
            }),
        ],
        // If initialContent is a string, treated as Markdown. If object, treated as JSON.
        content: initialContent !== undefined ? initialContent : `
# Episode Plan

Start writing your plan here...
        `,
        editorProps: {
            attributes: {
                class: 'prose prose-lg focus:outline-none max-w-none min-h-[50vh]',
            },
            handleDrop: (view, event, slice, moved) => {
                console.log('[SmartEditor TipTap handleDrop] Event fired', {
                    type: event.type,
                    dataTransferTypes: event.dataTransfer?.types,
                    clientX: event.clientX,
                    clientY: event.clientY,
                    moved
                });

                // If this is an internal move (repositioning within the editor),
                // let TipTap's default behavior handle it
                if (moved) {
                    console.log('[SmartEditor TipTap handleDrop] Internal move detected, letting default handler handle it');
                    return false;
                }

                // Handle external drops (from vault or context sidebar)
                if (event.dataTransfer) {
                    const coordinates = view.posAtCoords({ left: event.clientX, top: event.clientY });
                    console.log('[SmartEditor TipTap handleDrop] Coordinates:', coordinates);
                    if (!coordinates) {
                        console.log('[SmartEditor TipTap handleDrop] No coordinates, returning false');
                        return false;
                    }

                    let contentToInsert = null;
                    let droppedArtifact = null;

                    // Check for JSON data (from ContextSidebar research drops or VaultDrawer assets)
                    if (event.dataTransfer.types.includes('application/json')) {
                        try {
                            const jsonData = event.dataTransfer.getData('application/json');
                            console.log('[SmartEditor TipTap handleDrop] JSON data:', jsonData);
                            const item = JSON.parse(jsonData);

                            // Handle vault asset drops
                            if (item && item.type === 'vault-asset') {
                                console.log('[SmartEditor TipTap handleDrop] Vault asset detected:', item);
                                event.preventDefault();

                                droppedArtifact = {
                                    ...item,
                                    canvasPosition: {
                                        x: event.clientX,
                                        y: event.clientY
                                    },
                                    droppedAt: new Date().toISOString()
                                };

                                // Special handling for PDFs - use pre-extracted text content
                                // Check both mimeType and fileName extension for flexibility
                                const isPdf = item.mimeType === 'application/pdf' ||
                                    (item.fileName && item.fileName.toLowerCase().endsWith('.pdf'));

                                if (item.assetType === 'document' && isPdf) {
                                    console.log('[SmartEditor TipTap handleDrop] PDF detected, using cached text...', item);

                                    // Use pre-extracted text (cached on upload) for instant drop experience
                                    if (item.extractedText) {
                                        // Format the extracted text with a header
                                        const headerText = `### ${item.title || item.fileName || 'PDF Content'}\n\n`;
                                        const formattedText = headerText + item.extractedText + '\n\n';

                                        // Insert directly - no async needed!
                                        if (editorInstanceRef.current) {
                                            editorInstanceRef.current.chain()
                                                .focus()
                                                .insertContentAt(coordinates.pos, formattedText)
                                                .run();
                                        }

                                        console.log('[SmartEditor TipTap handleDrop] PDF text inserted successfully from cache');

                                        // Notify parent about the dropped artifact
                                        if (droppedArtifact && onArtifactDrop) {
                                            onArtifactDrop(droppedArtifact);
                                        }

                                        return true;
                                    } else {
                                        // Fallback: No cached text available (old PDFs uploaded before this feature)
                                        // Insert a link instead
                                        console.log('[SmartEditor TipTap handleDrop] No cached PDF text, inserting link');
                                        contentToInsert = `\n\n[${item.title || item.fileName}](${item.downloadUrl})\n\n`;
                                    }
                                }

                                // Generate content based on asset type
                                // For media types (image, video, audio), insert as media card
                                if (['image', 'video', 'audio'].includes(item.assetType) && item.downloadUrl) {
                                    // Insert as media card node with 80% default width
                                    const mediaCardAttrs = {
                                        src: item.downloadUrl,
                                        title: item.title || item.fileName,
                                        assetType: item.assetType,
                                        fileName: item.fileName,
                                        assetId: item.id,
                                        width: 80, // Default to 80% width
                                        altText: '',
                                        caption: ''
                                    };

                                    // Use the editor's insertContentAt to insert the media card
                                    if (editorInstanceRef.current) {
                                        editorInstanceRef.current.chain()
                                            .focus()
                                            .insertContentAt(coordinates.pos, {
                                                type: 'mediaCard',
                                                attrs: mediaCardAttrs
                                            })
                                            .run();
                                    }

                                    console.log('[SmartEditor TipTap handleDrop] Media card inserted:', mediaCardAttrs);

                                    // Notify parent about the dropped artifact
                                    if (droppedArtifact && onArtifactDrop) {
                                        onArtifactDrop(droppedArtifact);
                                    }

                                    // Return early - don't process plain text fallback for media cards
                                    return true;
                                }
                                // For text snippets, insert as markdown
                                else if (item.assetType === 'text' && (item.content || item.contentPreview)) {
                                    contentToInsert = `\n\n### ${item.title || 'Text Snippet'}\n\n${item.content || item.contentPreview}\n\n`;
                                }
                                // For documents (non-PDF), insert as link
                                else if (item.assetType === 'document' && item.downloadUrl) {
                                    contentToInsert = `\n\n[${item.title || item.fileName}](${item.downloadUrl})\n\n`;
                                }
                                // Fallback for other types
                                else if (item.downloadUrl) {
                                    contentToInsert = `\n\n[${item.title || item.fileName}](${item.downloadUrl})\n\n`;
                                }
                                console.log('[SmartEditor TipTap handleDrop] Content to insert:', contentToInsert);
                            }
                            // Handle research drops from ContextSidebar
                            else if (item && item.title && item.content) {
                                console.log('[SmartEditor TipTap handleDrop] Research item detected:', item);
                                event.preventDefault();

                                // Check if content is HTML (contains HTML tags)
                                if (item.content.includes('<') && item.content.includes('>')) {
                                    // Content is already HTML, use it directly with title
                                    contentToInsert = `<h3>${item.title}</h3>${item.content}`;
                                } else {
                                    // Format as markdown with title as heading
                                    contentToInsert = `\n### ${item.title}\n\n${item.content}\n`;
                                }
                            }
                        } catch (e) {
                            console.warn('[SmartEditor TipTap handleDrop] Failed to parse dropped JSON data:', e);
                        }
                    }

                    // Fallback: Check for plain text (markdown or HTML content)
                    if (!contentToInsert && event.dataTransfer.types.includes('text/plain')) {
                        const text = event.dataTransfer.getData('text/plain');
                        console.log('[SmartEditor TipTap handleDrop] Plain text data:', text?.substring(0, 100));
                        if (text) {
                            event.preventDefault();
                            contentToInsert = text;
                        }
                    }

                    // If we have content to insert (for non-media types or fallback)
                    if (contentToInsert && editorInstanceRef.current) {
                        console.log('[SmartEditor TipTap handleDrop] Inserting content at position:', coordinates.pos);
                        const editorInstance = editorInstanceRef.current;
                        const pos = coordinates.pos;

                        // For HTML content, use DOMParser with ProseMirror's DOMParser
                        if (contentToInsert.includes('<') && contentToInsert.includes('>')) {
                            try {
                                // Create a temporary div to parse HTML
                                const tempDiv = document.createElement('div');
                                tempDiv.innerHTML = contentToInsert;

                                // Use ProseMirror's DOMParser to convert DOM nodes to ProseMirror nodes
                                const parser = DOMParser.fromSchema(editorInstance.schema);
                                const proseMirrorNodes = parser.parse(tempDiv);

                                // Insert the parsed ProseMirror nodes
                                const transaction = view.state.tr.insert(pos, proseMirrorNodes.content);
                                view.dispatch(transaction);
                            } catch (htmlError) {
                                console.warn('[SmartEditor TipTap handleDrop] Failed to parse HTML, inserting as text:', htmlError);
                                // Strip HTML tags and insert as plain text
                                const plainText = contentToInsert.replace(/<[^>]*>/g, '');
                                view.dispatch(view.state.tr.insertText(plainText, pos));
                            }
                        } else {
                            // For markdown content, use the editor's insertContentAt
                            editorInstance.chain()
                                .focus()
                                .insertContentAt(pos, contentToInsert)
                                .run();
                        }
                    }

                    // Notify parent about the dropped artifact (for both media cards and text)
                    if (droppedArtifact && onArtifactDrop) {
                        onArtifactDrop(droppedArtifact);
                    }

                    console.log('[SmartEditor TipTap handleDrop] Content inserted successfully, returning true');
                    return true;
                }

                console.log('[SmartEditor TipTap handleDrop] No content inserted, returning false');
                // Return false to let the default behavior handle internal editor drag/drop
                return false;
            }
        },
    });

    // Store editor in ref for access in handleDrop - update synchronously
    if (editor) {
        editorInstanceRef.current = editor;
    }

    useImperativeHandle(ref, () => ({
        insertContent: (content) => {
            if (editor) {
                // Always insert at the end of the document to prevent progressive indentation
                // This ensures streaming content appends correctly without stair-step effect
                const endPosition = editor.state.doc.content.size;

                // Get current document text to check if there's existing content
                const currentText = editor.getText();
                const hasExistingContent = currentText.trim().length > 0;

                // Prepend newlines if there's existing content to ensure we start on a new line
                // This prevents overwriting or appending directly after existing text
                const contentToInsert = hasExistingContent
                    ? '\n\n' + content
                    : content;

                editor.chain()
                    .focus('end')
                    .insertContentAt(endPosition - 1, contentToInsert)
                    .run();
            }
        },
        focus: () => {
            editor?.commands.focus();
        },
        getMarkdown: () => {
            return editor?.storage.markdown?.getMarkdown() || '';
        }
    }));

    // Hydrate content from Firestore (initial load)
    useEffect(() => {
        if (editor && initialContent) {
            // Check if we are in the default state (approximate) or empty
            const currentContent = editor.getText().trim();
            const isDefault = editor.isEmpty ||
                currentContent === "Episode Plan" ||
                currentContent.includes("Start writing your plan here");

            // Only update if we are effectively "default" and the new content is different
            // Safety check: ensure markdown extension exists before getting markdown
            const markdownExtension = editor.storage.markdown;
            if (markdownExtension && isDefault) {
                const currentMarkdown = markdownExtension.getMarkdown();
                if (initialContent !== currentMarkdown) {
                    editor.commands.setContent(initialContent);
                }
            }
        }
    }, [initialContent, editor]);



    // Optimized Debounced Save Logic with proper cleanup
    useEffect(() => {
        if (!editor) return;

        const DEBOUNCE_TIME = 3000; // 3 seconds debounce
        const MIN_SAVE_INTERVAL = 5000; // Minimum 5 seconds between saves

        const performSave = async (markdown) => {
            // Skip if content hasn't changed
            if (markdown === lastSavedContentRef.current) {
                return;
            }

            // Skip if already saving
            if (isSavingRef.current) {
                return;
            }

            isSavingRef.current = true;
            setSavingStatus('saving');

            try {
                if (onSave) {
                    await onSave(markdown);
                    lastSavedContentRef.current = markdown;
                }
                setSavingStatus('saved');
            } catch (error) {
                console.error('Save failed:', error);
                setSavingStatus('unsaved');
            } finally {
                isSavingRef.current = false;
            }
        };

        const handleUpdate = () => {
            // Clear existing timeout
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }

            // Set saving status to unsaved immediately
            setSavingStatus('unsaved');

            // Set new timeout with longer debounce
            saveTimeoutRef.current = setTimeout(async () => {
                const markdownExtension = editor.storage.markdown;
                if (!markdownExtension) {
                    console.warn('Markdown extension storage not found');
                    return;
                }

                const markdown = markdownExtension.getMarkdown();
                await performSave(markdown);
            }, DEBOUNCE_TIME);
        };

        editor.on('update', handleUpdate);

        // Cleanup on unmount
        return () => {
            editor.off('update', handleUpdate);
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
        };
    }, [editor, onSave]);

    // Version History Auto-Save (every 15 minutes)
    useEffect(() => {
        if (!editor || !episodeId) return;

        const VERSION_SAVE_INTERVAL = 15 * 60 * 1000; // 15 minutes

        const saveVersion = async () => {
            const markdownExtension = editor.storage.markdown;
            if (!markdownExtension) return;

            const markdown = markdownExtension.getMarkdown();
            if (markdown && markdown.trim().length > 50) {
                try {
                    await versionHistoryService.saveVersion(episodeId, markdown, 'Auto-saved');
                } catch (err) {
                    console.warn('Failed to save version:', err.message);
                }
            }
        };

        versionSaveTimeoutRef.current = setInterval(saveVersion, VERSION_SAVE_INTERVAL);

        return () => {
            if (versionSaveTimeoutRef.current) {
                clearInterval(versionSaveTimeoutRef.current);
            }
        };
    }, [editor, episodeId]);

    // Handle version restore
    const handleRestoreVersion = useCallback((content) => {
        if (editor && content) {
            editor.commands.setContent(content);
        }
    }, [editor]);

    // Force re-render on selection update for toolbar active states
    useEffect(() => {
        if (!editor) return;

        const handleSelectionUpdate = () => {
            // Increment counter to force re-render
            setSelectionUpdate(prev => prev + 1);
        };

        editor.on('selectionUpdate', handleSelectionUpdate);

        return () => {
            editor.off('selectionUpdate', handleSelectionUpdate);
        };
    }, [editor]);

    // Track text selection for drag-to-vault functionality
    useEffect(() => {
        if (!editor) return;

        // Check if editor is mounted by checking isEditable
        let isMounted = true;
        let editorElement = null;

        const handleTextSelection = () => {
            if (!isMounted) return;

            try {
                const { from, to } = editor.state.selection;
                const text = editor.state.doc.textBetween(from, to, ' ');

                if (text.trim().length > 0) {
                    setSelectedText(text.trim());

                    // Notify parent of selection change for AI context awareness
                    if (onSelectionChange) {
                        onSelectionChange(text.trim());
                    }

                    // Get selection coordinates for drag handle positioning
                    const view = editor.view;
                    if (view) {
                        const start = view.coordsAtPos(from);
                        const end = view.coordsAtPos(to);

                        setSelectionCoords({
                            top: Math.min(start.top, end.top) - 40,
                            left: (start.left + end.left) / 2,
                            width: Math.abs(end.left - start.left)
                        });
                    }
                } else {
                    setSelectedText('');
                    setSelectionCoords(null);

                    // Notify parent of cleared selection
                    if (onSelectionChange) {
                        onSelectionChange(null);
                    }
                }
            } catch (err) {
                // Ignore errors if editor is unmounting
            }
        };

        editor.on('selectionUpdate', handleTextSelection);

        // Also handle mouseup for immediate feedback
        const handleMouseUp = () => {
            setTimeout(handleTextSelection, 10);
        };

        // Get editor element safely
        try {
            if (editor.view && editor.view.dom) {
                editorElement = editor.view.dom;
                editorElement.addEventListener('mouseup', handleMouseUp);
            }
        } catch (err) {
            // Editor not mounted yet
        }

        return () => {
            isMounted = false;
            editor.off('selectionUpdate', handleTextSelection);
            if (editorElement) {
                editorElement.removeEventListener('mouseup', handleMouseUp);
            }
        };
    }, [editor]);

    // Handle drag start for selected text
    const handleDragStart = useCallback((e) => {
        if (!selectedText) return;

        const artifact = {
            id: `artifact-${Date.now()}`,
            title: 'Unnamed', // Default title - user can edit after saving
            content: selectedText,
            createdAt: new Date().toISOString(),
            category: 'snippet'
        };

        e.dataTransfer.setData('application/json', JSON.stringify(artifact));
        e.dataTransfer.setData('text/plain', selectedText);
        e.dataTransfer.effectAllowed = 'copy';
    }, [selectedText]);

    // Track drag position to detect when dragging near right edge (for vault) or from vault
    const handleDrag = useCallback((e) => {
        const windowWidth = window.innerWidth;
        const rightEdgeThreshold = windowWidth - 100; // 100px from right edge

        // Check if dragging from vault (has vault-asset type in dataTransfer)
        // Only set to true if we're over the editor container
        if (e.dataTransfer && e.dataTransfer.types.includes('application/json')) {
            // Check if we're over the editor container
            if (editorContainerRef.current) {
                const rect = editorContainerRef.current.getBoundingClientRect();
                const isOverEditor = (
                    e.clientX >= rect.left &&
                    e.clientX <= rect.right &&
                    e.clientY >= rect.top &&
                    e.clientY <= rect.bottom
                );
                setIsDraggingFromVault(isOverEditor);
            }
        }

        if (e.clientX > rightEdgeThreshold && e.clientX < windowWidth) {
            setIsDraggingToVault(true);
            // Open vault when dragging near right edge
            if (onOpenVault) {
                onOpenVault();
            }
        } else {
            setIsDraggingToVault(false);
        }
    }, [onOpenVault]);

    // Handle drag end
    const handleDragEnd = useCallback(() => {
        setIsDraggingToVault(false);
        setIsDraggingFromVault(false);
    }, []);

    /**
     * Handle "Add to Vault" from paragraph controls
     * Returns a promise for proper async handling
     */
    const handleParagraphAddToVault = useCallback(async (text) => {
        if (!text) {
            throw new Error('No text provided');
        }

        try {
            // Direct call to vaultService to save the text with "Unnamed" as default title
            const result = await vaultService.uploadTextSnippet(text, 'Unnamed', episodeId);

            if (!result.success) {
                throw new Error(result.error || 'Failed to save to vault');
            }

            // Also call the prop if provided (for backward compatibility)
            if (onAddVaultArtifact) {
                const artifact = {
                    id: result.asset?.id || `artifact-${Date.now()}`,
                    title: 'Unnamed',
                    content: text,
                    createdAt: new Date().toISOString(),
                    category: 'snippet'
                };
                onAddVaultArtifact(artifact);
            }

            return result;
        } catch (err) {
            console.error('Failed to save to vault:', err);
            throw err;
        }
    }, [onAddVaultArtifact, episodeId]);

    /**
     * Handle "Add to Mariko" from paragraph controls
     * Sets the paragraph text as context for the AI assistant
     */
    const handleParagraphAddToMariko = useCallback((text) => {
        setMarikoContext(text);
        // Also notify parent if callback provided
        if (onAddToMariko) {
            onAddToMariko(text);
        }
    }, [onAddToMariko]);

    /**
     * Handle opening the AI assistant (Mariko) modal
     */
    const handleOpenMariko = useCallback(() => {
        // Dispatch custom event to open AI assistant
        // The AiAssistant component will listen for this
        window.dispatchEvent(new CustomEvent('openMariko'));
    }, []);

    // Memoize the paragraph actions context value
    const paragraphActionsValue = useMemo(() => ({
        onAddToVault: handleParagraphAddToVault,
        onAddToMariko: handleParagraphAddToMariko,
        onOpenVault: onOpenVault,
        onOpenMariko: handleOpenMariko,
    }), [handleParagraphAddToVault, handleParagraphAddToMariko, onOpenVault, handleOpenMariko]);

    // Add global drag tracking with production-quality auto-scroll
    // Inspired by Notion, Linear, Figma, and Coda's smooth scrolling implementations
    // Uses dragged element's edges (head and tail) as scroll triggers for intuitive UX
    useEffect(() => {
        let scrollAnimationId = null;
        let lastTimestamp = 0;
        let currentVelocity = 0; // Current scroll velocity (pixels per second)
        let targetVelocity = 0; // Target velocity based on cursor position

        // Configuration constants - tuned for natural feel
        const SCROLL_CONFIG = {
            // Zone boundaries (in pixels from edge)
            zoneOuter: 150,        // Start scrolling when element edge enters this zone
            zoneInner: 30,         // Maximum speed zone (closest to edge)
            zoneDead: 15,          // Dead zone at very edge (prevents content going past canvas)

            // Speed settings (in pixels per second)
            maxSpeed: 800,         // Maximum scroll speed
            minSpeed: 100,         // Minimum scroll speed when in zone

            // Physics settings
            acceleration: 8,       // How quickly velocity increases (higher = snappier)
            deceleration: 12,      // How quickly velocity decreases when stopping (higher = stops faster)
            velocityThreshold: 1,  // Minimum velocity before stopping completely
        };

        // Smooth easing function (ease-in-out-cubic) for natural acceleration
        const easeInOutCubic = (t) => {
            return t < 0.5
                ? 4 * t * t * t
                : 1 - Math.pow(-2 * t + 2, 3) / 2;
        };

        // Calculate target velocity based on element edge position within scroll zone
        const calculateTargetVelocity = (distanceFromEdge, isScrollingUp) => {
            const { zoneOuter, zoneInner, zoneDead, maxSpeed, minSpeed } = SCROLL_CONFIG;

            // Dead zone - no scrolling at very edge
            if (distanceFromEdge < zoneDead) {
                return 0;
            }

            // Outside scroll zone - no scrolling
            if (distanceFromEdge > zoneOuter) {
                return 0;
            }

            // Within scroll zone - calculate speed based on position
            // Closer to edge = faster scrolling
            let proximityRatio = 0;

            if (distanceFromEdge <= zoneInner) {
                // In the "fast zone" (closest to edge) - maximum speed
                // But still interpolate within this zone for smoothness
                proximityRatio = 1 - ((distanceFromEdge - zoneDead) / (zoneInner - zoneDead));
                proximityRatio = Math.max(0, Math.min(1, proximityRatio));
            } else {
                // In the "ramp zone" (between inner and outer) - gradual speed increase
                const rampPosition = (zoneOuter - distanceFromEdge) / (zoneOuter - zoneInner);
                proximityRatio = rampPosition * 0.7; // Max 70% speed in ramp zone
            }

            // Apply easing for natural acceleration curve
            const easedRatio = easeInOutCubic(proximityRatio);

            // Calculate final speed
            const speed = minSpeed + easedRatio * (maxSpeed - minSpeed);

            // Return velocity with direction
            return isScrollingUp ? -speed : speed;
        };

        // Main scroll animation loop with delta-time calculations
        const scrollLoop = (timestamp) => {
            if (!editorContainerRef.current) {
                scrollAnimationId = requestAnimationFrame(scrollLoop);
                return;
            }

            // Calculate delta time in seconds (cap at 100ms to prevent huge jumps)
            const deltaTime = lastTimestamp ? Math.min((timestamp - lastTimestamp) / 1000, 0.1) : 0.016;
            lastTimestamp = timestamp;

            // Smoothly interpolate current velocity towards target
            // This creates natural acceleration and deceleration
            const { acceleration, deceleration, velocityThreshold } = SCROLL_CONFIG;

            if (Math.abs(targetVelocity) > Math.abs(currentVelocity)) {
                // Accelerating
                const accelRate = acceleration * 60; // Per-frame rate
                const maxChange = accelRate * deltaTime * (Math.abs(targetVelocity) + 100);
                if (currentVelocity < targetVelocity) {
                    currentVelocity = Math.min(currentVelocity + maxChange, targetVelocity);
                } else {
                    currentVelocity = Math.max(currentVelocity - maxChange, targetVelocity);
                }
            } else {
                // Decelerating
                const decelRate = deceleration * 60;
                const maxChange = decelRate * deltaTime * (Math.abs(currentVelocity) + 50);
                if (currentVelocity < targetVelocity) {
                    currentVelocity = Math.min(currentVelocity + maxChange, targetVelocity);
                } else {
                    currentVelocity = Math.max(currentVelocity - maxChange, targetVelocity);
                }
            }

            // Stop if velocity is negligible
            if (Math.abs(currentVelocity) < velocityThreshold && Math.abs(targetVelocity) < velocityThreshold) {
                currentVelocity = 0;
                targetVelocity = 0;
                scrollAnimationId = requestAnimationFrame(scrollLoop);
                return;
            }

            // Find the scrollable parent
            const scrollContainer = editorContainerRef.current.closest('.overflow-y-auto') ||
                editorContainerRef.current.closest('.overflow-auto') ||
                editorContainerRef.current.closest('[style*="overflow-y: auto"]') ||
                editorContainerRef.current.closest('[style*="overflow: auto"]') ||
                document.scrollingElement ||
                document.documentElement;

            // Apply scroll with delta-time for consistent speed across frame rates
            const scrollAmount = currentVelocity * deltaTime;

            // Check scroll boundaries to prevent over-scrolling
            const maxScroll = scrollContainer.scrollHeight - scrollContainer.clientHeight;
            const newScrollTop = scrollContainer.scrollTop + scrollAmount;

            if (newScrollTop >= 0 && newScrollTop <= maxScroll) {
                scrollContainer.scrollTop = newScrollTop;
            } else {
                // Hit boundary - stop velocity
                currentVelocity = 0;
                targetVelocity = 0;
            }

            scrollAnimationId = requestAnimationFrame(scrollLoop);
        };

        // Track cursor position and update target velocity
        let lastCursorY = 0;
        let cursorInZone = false;

        const handleGlobalDrag = (e) => {
            handleDrag(e);

            if (!editorContainerRef.current) return;

            const rect = editorContainerRef.current.getBoundingClientRect();
            const cursorY = e.clientY;
            lastCursorY = cursorY;

            // Get element dimensions from shared drag state
            // This is set by VaultDrawer when drag starts
            const dragState = getDragState();
            const elementHeight = dragState?.height || 0;

            // Calculate element edges based on cursor position and element dimensions
            // The cursor is typically at the top-left or center of the dragged element
            // We assume cursor is near the top of the element (common for drag previews)

            // Element head (top edge) - cursor position
            // Assuming cursor is roughly at the top portion of the drag preview
            const elementHeadY = cursorY;

            // Element tail (bottom edge) - cursor position plus element height
            const elementTailY = cursorY + elementHeight;

            // Calculate distances from container edges to element edges
            const headDistanceFromTop = elementHeadY - rect.top;
            const tailDistanceFromBottom = rect.bottom - elementTailY;

            // Also track cursor distance for fallback (when no element dimensions)
            const cursorDistanceFromTop = cursorY - rect.top;
            const cursorDistanceFromBottom = rect.bottom - cursorY;

            // Determine which edge is closer to triggering scroll
            // Use element edges if we have dimensions, otherwise fall back to cursor
            let topDistance, bottomDistance;

            if (elementHeight > 0) {
                // Use element edges for more intuitive scrolling
                topDistance = headDistanceFromTop;
                bottomDistance = tailDistanceFromBottom;
            } else {
                // Fallback to cursor position
                topDistance = cursorDistanceFromTop;
                bottomDistance = cursorDistanceFromBottom;
            }

            // Determine which zone we're in and calculate target velocity
            // Check if either edge is in the scroll zone
            if (topDistance < SCROLL_CONFIG.zoneOuter && topDistance >= SCROLL_CONFIG.zoneDead) {
                // Element head near top edge - scroll up
                targetVelocity = calculateTargetVelocity(topDistance, true);
                cursorInZone = true;
            } else if (bottomDistance < SCROLL_CONFIG.zoneOuter && bottomDistance >= SCROLL_CONFIG.zoneDead) {
                // Element tail near bottom edge - scroll down
                targetVelocity = calculateTargetVelocity(bottomDistance, false);
                cursorInZone = true;
            } else {
                // Outside scroll zone - decelerate to stop
                targetVelocity = 0;
                cursorInZone = false;
            }

            // Start animation loop if not already running
            if (!scrollAnimationId) {
                lastTimestamp = 0;
                scrollAnimationId = requestAnimationFrame(scrollLoop);
            }
        };

        const handleGlobalDragEnd = () => {
            // Smoothly decelerate to stop
            targetVelocity = 0;
            cursorInZone = false;

            // Don't immediately cancel - let the scroll loop decelerate naturally
            // But cancel after a short delay if velocity is already low
            setTimeout(() => {
                if (scrollAnimationId && Math.abs(currentVelocity) < SCROLL_CONFIG.velocityThreshold * 10) {
                    cancelAnimationFrame(scrollAnimationId);
                    scrollAnimationId = null;
                    currentVelocity = 0;
                    lastTimestamp = 0;
                }
            }, 100);

            handleDragEnd();
        };

        // Immediate stop on drop
        const handleGlobalDrop = () => {
            if (scrollAnimationId) {
                cancelAnimationFrame(scrollAnimationId);
                scrollAnimationId = null;
            }
            currentVelocity = 0;
            targetVelocity = 0;
            lastTimestamp = 0;
            // Clear drag state from shared module
            clearDragState();
            handleDragEnd();
        };

        document.addEventListener('dragover', handleGlobalDrag);
        document.addEventListener('dragend', handleGlobalDragEnd);
        document.addEventListener('drop', handleGlobalDrop);

        return () => {
            document.removeEventListener('dragover', handleGlobalDrag);
            document.removeEventListener('dragend', handleGlobalDragEnd);
            document.removeEventListener('drop', handleGlobalDrop);
            if (scrollAnimationId) {
                cancelAnimationFrame(scrollAnimationId);
            }
        };
    }, [handleDrag, handleDragEnd]);

    if (!editor) {
        return null;
    }

    return (
        <div
            ref={editorContainerRef}
            className="relative w-full max-w-4xl mx-auto pb-32 pt-16"
            onDragOver={(e) => {
                // Allow drop for both vault assets and context sidebar research
                if (e.dataTransfer.types.includes('application/json') || e.dataTransfer.types.includes('text/plain')) {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'copy';
                    setIsDraggingFromVault(true);
                }
            }}
            onDragLeave={(e) => {
                // Check if we're leaving the editor container
                const rect = editorContainerRef.current?.getBoundingClientRect();
                if (rect) {
                    const x = e.clientX;
                    const y = e.clientY;
                    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
                        setIsDraggingFromVault(false);
                    }
                }
            }}
        >

            {/* Save Status Indicator & Version History Button */}
            <div className="fixed top-4 right-20 flex items-center gap-2 z-30">
                {/* Version History Button */}
                <button
                    onClick={() => setIsVersionHistoryOpen(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-white rounded-full shadow-sm border border-slate-200 text-xs font-medium text-slate-500 hover:text-amber-600 hover:border-amber-200 transition-all duration-300"
                    title="Version History"
                >
                    <History size={14} />
                </button>

                {/* Save Status */}
                <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-full shadow-sm border border-slate-200 text-xs font-medium text-slate-500 transition-all duration-300">
                    {savingStatus === 'saved' && (
                        <>
                            <Cloud size={14} className="text-slate-400" />
                            <span>Saved</span>
                        </>
                    )}
                    {savingStatus === 'saving' && (
                        <>
                            <Loader size={14} className="animate-spin text-indigo-500" />
                            <span className="text-indigo-600">Saving...</span>
                        </>
                    )}
                    {savingStatus === 'unsaved' && (
                        <>
                            <div className="w-2 h-2 rounded-full bg-amber-400" />
                            <span className="text-amber-600">Unsaved changes...</span>
                        </>
                    )}
                </div>
            </div>

            {/* Version History Panel */}
            <VersionHistoryPanel
                isOpen={isVersionHistoryOpen}
                onClose={() => setIsVersionHistoryOpen(false)}
                episodeId={episodeId}
                onRestoreVersion={handleRestoreVersion}
                currentContent={editor?.storage?.markdown?.getMarkdown()}
            />

            {/* Formatting Toolbar - Lower z-index to not overlap AI assistant (z-50) */}
            <EditorToolbar editor={editor} />

            {/* Editor with Paragraph Actions Context */}
            <ParagraphActionsContext.Provider value={paragraphActionsValue}>
                <div className="min-h-[500px] mt-4 pl-8">
                    <EditorContent editor={editor} />
                </div>
            </ParagraphActionsContext.Provider>
        </div>
    );
});

SmartEditor.displayName = 'SmartEditor';

export default SmartEditor;
